// src/Components/moderation/BlockedUsersPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Ban, Unlock, RefreshCw, ShieldOff, AlertTriangle } from 'lucide-react';
import { moderationAPI } from '../../services/api';

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg ${className}`} />
);

export default function BlockedUsersPage() {
  const [blocks, setBlocks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [warning, setWarning] = useState(null);
  const [page, setPage]       = useState(1);
  const [actioningId, setActioningId] = useState(null);
  const [confirmId, setConfirmId]     = useState(null);

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await moderationAPI.listAllBlocks(page, 20);
      if (res.success) {
        setBlocks(res.data.blocks || []);
        setWarning(res.data.warning || null);
      }
    } catch (err) {
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

  const handleUnblock = async (blockId) => {
    setActioningId(blockId);
    try {
      await moderationAPI.forceUnblock(blockId);
      await fetchBlocks();
    } catch (err) {
      alert(`Unblock failed: ${err}`);
    } finally {
      setActioningId(null);
      setConfirmId(null);
    }
  };

  const timeAgo = (ts) => {
    if (!ts) return 'Unknown';
    const m = Math.floor((Date.now() - new Date(ts)) / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <ShieldOff className="text-red-600" size={24} />
            Blocked Users
          </h1>
          <p className="text-sm text-gray-500">Platform-wide user block relationships</p>
        </div>
        <button onClick={fetchBlocks}
          className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-sm hover:border-red-400 transition">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <strong>Backend note:</strong> {warning} — likely means the <code>user_blocks</code> table
            doesn't exist yet in this database.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm">
          <strong>Error:</strong> {error}
          <button onClick={fetchBlocks} className="ml-3 underline">Retry</button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        {loading ? (
          <div className="space-y-3">
            {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : blocks.length === 0 ? (
          <div className="text-center py-16">
            <ShieldOff size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No blocked users found</p>
            <p className="text-gray-300 text-sm mt-1">Block relationships will appear here as users block each other.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {blocks.map((block) => (
              <div key={block.id}
                className="flex items-center justify-between gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-gray-700 dark:text-gray-200">{block.blocker_id}</span>
                    <Ban size={14} className="text-red-400" />
                    <span className="font-mono text-gray-700 dark:text-gray-200">{block.blocked_id}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Blocked {timeAgo(block.created_at)}</p>
                </div>
                <button
                  disabled={actioningId === block.id}
                  onClick={() => setConfirmId(block.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-100 transition disabled:opacity-50">
                  <Unlock size={13} /> Force Unblock
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Force Unblock?</h3>
            <p className="text-sm text-gray-500 mb-5">
              This removes the block relationship. Use this for dispute resolution only —
              both users will be able to see and message each other again.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmId(null)}
                className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition">
                Cancel
              </button>
              <button onClick={() => handleUnblock(confirmId)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                Confirm Unblock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}